/*
 * ComingSoon — PrepareUp.ai's public pre-launch page (signed-out visitors).
 *
 * One page: minimal header (brand + Coming Soon) · hero — the promise on the
 * left, six product cards with small sample-UI previews on the right · a
 * "who is it for" role carousel · 1:1 guidance from the founders · footer.
 *
 * The previews are illustrative markup with sample content (the interviewer
 * is a simulated portrait). Motion is restrained — the cards settle in once,
 * the flashcard turns over, the rapid-fire timer ticks — and all of it stops
 * under prefers-reduced-motion. Links and photos come from
 * src/config/comingSoon.js. There is no visible sign-in on this page; the
 * team can still reach the login at #signin.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/coming-soon.css';
import { COMING_SOON } from '../config/comingSoon';
import { IconBook, IconCode, IconList } from './nano/icons';

const Svg = ({ size = 20, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">{children}</svg>
);
const IconVideo = (p) => <Svg {...p}><rect x="3" y="6" width="13" height="12" rx="2.5" /><path d="m16 10.5 5-3v9l-5-3" /></Svg>;
const IconMic = (p) => <Svg {...p}><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" /></Svg>;
const IconExternal = (p) => <Svg {...p}><path d="M7 17 17 7M9 7h8v8" /></Svg>;
const IconPhoneOff = (p) => <Svg {...p}><path d="M3 14.5c5-4.6 13-4.6 18 0l-2.4 2.3-3.1-1.6v-2.4a11 11 0 0 0-7 0v2.4l-3.1 1.6Z" /></Svg>;
const IconBolt = (p) => <Svg {...p}><path d="M13 3 5 13.5h6L10 21l8-10.5h-6Z" /></Svg>;
const IconBulb = (p) => <Svg {...p}><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-3.6 10.8c.7.6 1.1 1.3 1.1 2.2h5c0-.9.4-1.6 1.1-2.2A6 6 0 0 0 12 3Z" /></Svg>;
const IconHub = (p) => <Svg {...p}><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H10l2 2h6.5A1.5 1.5 0 0 1 20 7.5v11a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5Z" /><path d="M8 12h8M8 15.5h5" /></Svg>;
const IconTimer = (p) => <Svg {...p}><circle cx="12" cy="13.5" r="7" /><path d="M12 10v3.5l2 1.5M10 3h4" /></Svg>;
const IconPlay = (p) => <Svg {...p}><path d="M8 5.5v13l10.5-6.5Z" /></Svg>;
const IconChevron = ({ dir = 'right', ...p }) => <Svg {...p}><path d={dir === 'left' ? 'm15 6-6 6 6 6' : 'm9 6 6 6-6 6'} /></Svg>;
const IconNote = (p) => <Svg {...p}><path d="M6 3.5h9l3 3V20a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 20Z" /><path d="M9 10h6M9 13.5h6M9 17h3" /></Svg>;
const IconBookmark = (p) => <Svg {...p}><path d="M7 4h10v16l-5-3.5L7 20Z" /></Svg>;
const IconSparkle = (p) => <Svg {...p}><path d="M12 4v4M12 16v4M4 12h4M16 12h4" /><path d="M12 9a3 3 0 0 0 3 3 3 3 0 0 0-3 3 3 3 0 0 0-3-3 3 3 0 0 0 3-3Z" /></Svg>;
const IconBars = (p) => <Svg {...p}><path d="M5 20V12M10 20V6M15 20v-9M20 20V9" /></Svg>;
const IconLayers = (p) => <Svg {...p}><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></Svg>;
const IconUser = (p) => <Svg {...p}><circle cx="12" cy="8.5" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" /></Svg>;
const IconPen = (p) => <Svg {...p}><path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17Z" /><path d="m14 7 3 3" /></Svg>;
const IconChip = (p) => <Svg {...p}><rect x="7" y="7" width="10" height="10" rx="2" /><path d="M10 3v4M14 3v4M10 17v4M14 17v4M3 10h4M3 14h4M17 10h4M17 14h4" /></Svg>;
const IconChart = (p) => <Svg {...p}><path d="M4 19h16" /><path d="m5 15 4.5-4.5 3.5 3L19 7" /></Svg>;
const IconTable = (p) => <Svg {...p}><rect x="4" y="5" width="16" height="14" rx="1.5" /><path d="M4 10h16M10 10v9" /></Svg>;

const useReducedMotion = () => {
  const mq = useMemo(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null), []);
  const [reduced, setReduced] = useState(() => !!mq?.matches);
  useEffect(() => {
    if (!mq) return undefined;
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [mq]);
  return reduced;
};

/* ── the six product previews (sample content, decorative) ──────────────── */
const McqFlashPreview = ({ reduced }) => {
  const [back, setBack] = useState(false);
  useEffect(() => {
    if (reduced) return undefined;
    const t = setInterval(() => setBack((b) => !b), 4200);
    return () => clearInterval(t);
  }, [reduced]);
  return (
    <div className="pv pv-mcq">
      <div className="pv-mcq-q">
        <p>Which of the following is a primary key in a relational database?</p>
        <ul>
          <li className="on"><i />Unique per row</li>
          <li><i />Allows duplicates</li>
          <li><i />Stores long text</li>
          <li><i />Links two tables</li>
        </ul>
      </div>
      <div className="pv-flash">
        <div className={`pv-flash-card${back ? ' is-back' : ''}`}>
          <span className="pv-flash-face front"><small>Flashcard</small><b>Primary key</b><em>Tap to reveal</em></span>
          <span className="pv-flash-face back"><small>Answer</small><span>Uniquely identifies each record in a table.</span></span>
        </div>
        <span className="pv-pager"><IconChevron dir="left" size={11} /> 1/20 <IconChevron size={11} /></span>
      </div>
    </div>
  );
};

const TheoryPreview = () => (
  <div className="pv pv-theory">
    <div className="pv-tabs"><span className="on">Theory</span><span>Case Study</span></div>
    <p>Explain the bias–variance tradeoff in machine learning.</p>
    <span className="pv-input">Write your answer…</span>
    <span className="pv-btn">Submit</span>
  </div>
);

const RapidPreview = ({ reduced }) => {
  const [left, setLeft] = useState(28);
  useEffect(() => {
    if (reduced) return undefined;
    const t = setInterval(() => setLeft((s) => (s <= 1 ? 30 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [reduced]);
  return (
    <div className="pv pv-rapid">
      <div className="pv-rapid-top"><span><IconTimer size={14} /> 00:{String(left).padStart(2, '0')}</span><em>3/10</em></div>
      <span className="pv-bar"><i style={{ width: `${(left / 30) * 100}%` }} /></span>
      <p>What is the difference between precision and recall?</p>
      <span className="pv-input">Type your answer…</span>
      <span className="pv-btn">Next</span>
    </div>
  );
};

const CODE = [
  [<><span className="k">def</span> two_sum(a, t):</>],
  [<>{'  '}seen = {'{}'}</>],
  [<>{'  '}<span className="k">for</span> i, x <span className="k">in</span> enumerate(a):</>],
  [<>{'    '}<span className="k">if</span> t - x <span className="k">in</span> seen:</>],
  [<>{'      '}<span className="k">return</span> [seen[t - x], i]</>],
  [<>{'    '}seen[x] = i</>],
];
const CodePreview = () => (
  <div className="pv pv-code">
    <div className="pv-code-ed">
      <span className="pv-code-lang">Python</span>
      <ol>{CODE.map(([line], k) => <li key={k}><code>{line}</code></li>)}</ol>
    </div>
    <div className="pv-code-foot"><span>Test cases · 3 of 3 passed</span><span className="pv-btn"><IconPlay size={11} /> Run</span></div>
  </div>
);

const HubPreview = () => (
  <div className="pv pv-hub">
    {[[IconNote, 'My Notes'], [IconBookmark, 'Saved Content'], [IconSparkle, 'Interview Insights'], [IconBook, 'Quick Revision']].map(([I, t]) => (
      <span key={t} className="pv-hub-row"><I size={15} /><b>{t}</b><IconChevron size={12} /></span>
    ))}
  </div>
);

const VideoPreview = ({ image }) => (
  <div className="pv pv-video">
    <div className="pv-video-tile">
      {image ? <img src={image} alt="" /> : null}
      <span className="pv-video-time">02:18</span>
      <span className="pv-video-ctrl"><i><IconMic size={10} /></i><i><IconVideo size={10} /></i><i className="end"><IconPhoneOff size={10} /></i></span>
    </div>
    <div className="pv-video-side">
      <span className="pv-video-head"><b>AI Interview</b><em><i /> In progress</em></span>
      <p>Tell me about a time you used data to solve a real business problem.</p>
      <span className="pv-transcript"><b>You</b> At my last role, churn rose 8%, so I…</span>
    </div>
  </div>
);

const FEATURES = [
  { id: 'mcq', Icon: IconList, tone: 'blue', title: 'MCQs & Flashcards', text: 'Test your knowledge, revise key concepts, and strengthen your understanding.', Preview: McqFlashPreview },
  { id: 'theory', Icon: IconBulb, tone: 'violet', title: 'Theory & Case Studies', text: 'Explain concepts, apply what you’ve learned, and work through real-world problems.', Preview: TheoryPreview },
  { id: 'rapid', Icon: IconBolt, tone: 'green', title: 'AI Rapid Fire', text: 'Answer quick, timed questions to improve recall, speed, and confidence.', Preview: RapidPreview },
  { id: 'code', Icon: IconCode, tone: 'red', title: 'Coding Practice', text: 'Solve coding problems, test your solutions, and improve your problem-solving skills.', Preview: CodePreview },
  { id: 'hub', Icon: IconHub, tone: 'amber', title: 'Knowledge Hub', text: 'Save your notes, organise learning resources, and revisit useful insights from your preparation and AI interviews.', Preview: HubPreview },
  { id: 'video', Icon: IconVideo, tone: 'blue', title: 'AI Video Interviews', text: 'Practise realistic video interviews and receive AI-powered feedback.', Preview: VideoPreview },
];

const ROLES = [
  { id: 'pm', Icon: IconLayers, title: 'Product Manager', text: 'Practise product thinking and case questions.' },
  { id: 'uxr', Icon: IconUser, title: 'UX Researcher', text: 'Prepare for research methods, scenarios, and interviews.' },
  { id: 'design', Icon: IconPen, title: 'UI/UX Designer', text: 'Strengthen design thinking and portfolio discussions.' },
  { id: 'ds', Icon: IconBars, title: 'Data Scientist', text: 'Practise data concepts, analytical thinking, and technical questions.' },
  { id: 'ai', Icon: IconChip, title: 'AI Engineer', text: 'Prepare for AI, ML, and technical problem-solving.' },
  { id: 'swe', Icon: IconCode, title: 'Software Engineer', text: 'Practise coding and technical interview questions.' },
  { id: 'ba', Icon: IconChart, title: 'Business Analyst', text: 'Practise requirements, metrics, and stakeholder scenarios.' },
  { id: 'da', Icon: IconTable, title: 'Data Analyst', text: 'Prepare for SQL, dashboards, and analytical case questions.' },
];

/*
 * RoleCarousel — one card at a time, at a calm pace. Seamless loop: the first
 * few cards are repeated off-screen after the last, and once the track lands
 * on those copies it snaps back to the start without a transition, so no role
 * is ever visible twice. Auto-advance pauses on hover, keyboard focus or touch,
 * and while the tab is hidden; under reduced motion it never auto-advances.
 * Swipe on touch screens; arrows and dots work with a keyboard.
 */
const GAP = 14;
const RoleCarousel = ({ items, reduced }) => {
  const n = items.length;
  const viewport = useRef(null);
  const [perView, setPerView] = useState(4);
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [held, setHeld] = useState({ hover: false, focus: false, touch: false });
  const [hidden, setHidden] = useState(false);
  const touchX = useRef(null);
  const touchTimer = useRef(null);

  useEffect(() => {
    const on = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', on);
    return () => { document.removeEventListener('visibilitychange', on); clearTimeout(touchTimer.current); };
  }, []);
  useEffect(() => {
    const el = viewport.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => {
      const w = e.contentRect.width;
      setPerView(w >= 860 ? 5 : w >= 660 ? 4 : w >= 470 ? 3 : w >= 300 ? 2 : 1);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const smooth = !reduced;
  const at = useRef(0);
  at.current = index;
  const next = useCallback(() => {
    if (!smooth) { setIndex((at.current + 1) % n); return; }
    if (at.current >= n) return;                  // on the copies: the reset is about to land
    setAnimate(true);
    setIndex(at.current + 1);
  }, [smooth, n]);
  const prev = useCallback(() => {
    if (!smooth) { setIndex((at.current - 1 + n) % n); return; }
    if (at.current > 0) { setAnimate(true); setIndex(at.current - 1); return; }
    setAnimate(false);                            // jump (invisibly) onto the copies, then slide back one
    setIndex(n);
    requestAnimationFrame(() => requestAnimationFrame(() => { setAnimate(true); setIndex(n - 1); }));
  }, [smooth, n]);
  const onTransitionEnd = (e) => {
    if (e.target !== e.currentTarget || index < n) return;
    setAnimate(false);
    setIndex(0);
    requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
  };

  const paused = held.hover || held.focus || held.touch || hidden;
  useEffect(() => {
    if (reduced || paused) return undefined;
    const t = setInterval(next, 3600);
    return () => clearInterval(t);
  }, [reduced, paused, next]);

  const hold = (k, v) => setHeld((h) => (h[k] === v ? h : { ...h, [k]: v }));
  const active = index % n;
  const track = smooth ? [...items, ...items.slice(0, perView)] : items;

  return (
    <div className="cs-roles-car" aria-roledescription="carousel" aria-label="Roles PrepareUp.ai helps you prepare for"
      onMouseEnter={() => hold('hover', true)} onMouseLeave={() => hold('hover', false)}
      onFocus={() => hold('focus', true)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) hold('focus', false); }}>
      <div className="cs-roles-body">
        <button type="button" className="cs-roles-arrow" onClick={prev} aria-label="Previous role"><IconChevron dir="left" size={18} /></button>
        <div className="cs-roles-viewport" ref={viewport}
          onTouchStart={(e) => { clearTimeout(touchTimer.current); touchX.current = e.touches[0].clientX; hold('touch', true); }}
          onTouchEnd={(e) => {
            const x = e.changedTouches[0].clientX;
            const dx = x - (touchX.current ?? x);
            if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
            touchX.current = null;
            touchTimer.current = setTimeout(() => hold('touch', false), 2500);
          }}>
          <ul className={`cs-roles-track${animate && smooth ? ' is-animated' : ''}`}
            style={{ '--per': perView, '--gap': `${GAP}px`, transform: `translateX(calc(${-index} * (100% + ${GAP}px) / ${perView}))` }}
            onTransitionEnd={onTransitionEnd} aria-live={paused || reduced ? 'polite' : 'off'}>
            {track.map(({ id, Icon, title, text }, k) => {
              const copy = k >= n;
              return (
                <li key={`${id}-${k}`} className="cs-role" role={copy ? undefined : 'group'} aria-roledescription={copy ? undefined : 'slide'}
                  aria-label={copy ? undefined : `${k + 1} of ${n}: ${title}`} aria-hidden={copy || undefined}>
                  <span className="cs-role-icon" aria-hidden="true"><Icon size={19} /></span>
                  <b>{title}</b>
                  <small>{text}</small>
                </li>
              );
            })}
          </ul>
        </div>
        <button type="button" className="cs-roles-arrow" onClick={next} aria-label="Next role"><IconChevron size={18} /></button>
      </div>
      <div className="cs-roles-dots" role="group" aria-label="Choose a role">
        {items.map((it, k) => (
          <button key={it.id} type="button" className={k === active ? 'on' : ''} aria-label={`Show ${it.title}`} aria-current={k === active || undefined}
            onClick={() => { setAnimate(true); setIndex(k); }} />
        ))}
      </div>
    </div>
  );
};

const initialsOf = (name) => name.split(' ').map((w) => w[0]).slice(0, 2).join('');

export default function ComingSoon({ onSignIn }) {
  const year = new Date().getFullYear();
  const reduced = useReducedMotion();
  const { youtubeUrl, instagramUrl, interviewerImage, guides, approxDays } = COMING_SOON;

  useEffect(() => {
    const prev = document.title;
    document.title = 'PrepareUp.ai — Coming soon';
    return () => { document.title = prev; };
  }, []);

  // No visible sign-in on the public page; the team reaches the login at #signin.
  useEffect(() => {
    if (!onSignIn) return undefined;
    const check = () => { if (window.location.hash === '#signin') onSignIn(); };
    check();
    window.addEventListener('hashchange', check);
    return () => window.removeEventListener('hashchange', check);
  }, [onSignIn]);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    el.focus({ preventScroll: true });
  };

  return (
    <div className="cs-root">
      <a className="cs-skip" href="#cs-main" onClick={scrollTo('cs-main')}>Skip to content</a>

      <header className="cs-header">
        <a className="cs-brand" href="#top" onClick={scrollTo('cs-main')} aria-label="PrepareUp.ai home">
          <span className="cs-mark" aria-hidden="true">P</span>
          <span className="cs-word">PrepareUp<span>.ai</span></span>
        </a>
        <span className="cs-badge">Coming Soon</span>
      </header>

      <main id="cs-main" tabIndex={-1}>
        <section className="cs-hero" aria-labelledby="cs-title">
          <div className="cs-hero-copy">
            <p className="cs-eyebrow">Coming soon</p>
            <h1 id="cs-title">One place to prepare for <span>what’s next.</span></h1>
            <p className="cs-lede">Learn concepts, practice real-world questions, strengthen your skills, and prepare for interviews — all in one place.</p>
          </div>

          <div className="cs-features-wrap">
            <ul className={`cs-features${reduced ? '' : ' is-entering'}`} aria-label="What PrepareUp.ai will offer">
              {FEATURES.map(({ id, Icon, tone, title, text, Preview }, k) => (
                <li key={id} className={`cs-feature t-${tone}`} style={{ '--i': k }}>
                  <div className="cs-feature-head">
                    <span className="cs-feature-icon" aria-hidden="true"><Icon size={20} /></span>
                    <div><h2>{title}</h2><p>{text}</p></div>
                  </div>
                  <div className="cs-feature-preview" aria-hidden="true">
                    <Preview reduced={reduced} image={interviewerImage} />
                  </div>
                </li>
              ))}
            </ul>
            <p className="cs-caption">Previews show sample content.</p>
          </div>
        </section>

        <section className="cs-roles" aria-labelledby="cs-roles-h">
          <div className="cs-roles-copy">
            <p className="cs-eyebrow">Who is it for?</p>
            <h2 id="cs-roles-h">Different goals.<br /><span>Same preparation.</span></h2>
            <p>Whether you’re changing careers, preparing for interviews, or strengthening your skills, PrepareUp.ai helps you prepare for what’s next.</p>
          </div>
          <RoleCarousel items={ROLES} reduced={reduced} />
        </section>

        <section className="cs-guide" id="guidance" tabIndex={-1} aria-labelledby="cs-guide-h">
          <div className="cs-guide-copy">
            <p className="cs-eyebrow">Need help right now?</p>
            <h2 id="cs-guide-h">Get 1:1 guidance while we build PrepareUp.ai.</h2>
            <p>We’re working on PrepareUp.ai and plan to launch in approximately {approxDays} days. In the meantime, we’re available for personalized guidance on interview preparation, career direction and skill building through Topmate.</p>
          </div>
          <div className="cs-people">
            {guides.map((g) => (
              <article key={g.id} className="cs-person-card" aria-labelledby={`cs-${g.id}`}>
                <div className="cs-person-top">
                  {g.photo
                    ? <img className="cs-photo" src={g.photo} alt={`${g.name}, ${g.title}`} width="72" height="72" loading="lazy" />
                    : <span className="cs-photo cs-initials" role="img" aria-label={`${g.name} — photo coming soon`}>{initialsOf(g.name)}</span>}
                  <div><h3 id={`cs-${g.id}`}>{g.name}</h3><p>{g.title}</p></div>
                </div>
                <ul className="cs-tags" aria-label={`${g.name}’s areas`}>
                  {g.expertise.map((t) => <li key={t}>{t}</li>)}
                </ul>
                {g.topmateUrl ? (
                  <a className="cs-btn cs-btn-primary cs-book" href={g.topmateUrl} target="_blank" rel="noopener noreferrer">
                    Book on Topmate <IconExternal size={15} /><span className="cs-sr"> (opens in a new tab)</span>
                  </a>
                ) : (
                  <span className="cs-btn cs-btn-primary cs-book is-soon" aria-disabled="true">Topmate link coming soon</span>
                )}
              </article>
            ))}
          </div>
        </section>

        <p className="cs-closing">
          Your next big opportunity won’t prepare for itself. <span aria-hidden="true">😉</span><br />
          <span>We’re working on that part.</span>
        </p>
      </main>

      <footer className="cs-footer">
        <span className="cs-foot-brand"><span className="cs-mark sm" aria-hidden="true">P</span> PrepareUp.ai · Coming soon</span>
        <span className="cs-foot-links">
          {youtubeUrl && <a href={youtubeUrl} target="_blank" rel="noopener noreferrer">YouTube</a>}
          {instagramUrl && <a href={instagramUrl} target="_blank" rel="noopener noreferrer">Instagram</a>}
          <span>© {year} PrepareUp.ai</span>
        </span>
      </footer>
    </div>
  );
}
